// Web Audio API sound pack — zero external assets, all synthesized via oscillators.
// Gated behind a localStorage flag and a runtime Settings toggle.
//
// Design intent:
//  • click        — 40 ms soft sine; gentle tap feedback on button press.
//  • correct      — rising C-major triad (C5-E5-G5), bell-like; celebratory, not saccharine.
//  • incorrect    — low warm triangle thud, NOT a harsh buzz; 'hmm, think again' shape.
//  • chapterDone  — 4-note arpeggio (C5 E5 G5 C6), short and triumphant.
//
// Usage:
//   const { enabled, toggle, playClick, playCorrect, playIncorrect, playChapterComplete } = useSounds();

import { useCallback, useRef, useState } from 'react';

const LS_KEY = 'lernwelt.sounds.enabled';

type AudioCtxCtor = typeof AudioContext;

function initialEnabled(): boolean {
  try { return localStorage.getItem(LS_KEY) !== '0'; } catch { return true; }
}

function getCtxCtor(): AudioCtxCtor | null {
  if (typeof window === 'undefined') return null;
  const w = window as unknown as { AudioContext?: AudioCtxCtor; webkitAudioContext?: AudioCtxCtor };
  return w.AudioContext ?? w.webkitAudioContext ?? null;
}

function playTone(
  ctx: AudioContext,
  freq: number,
  durationMs: number,
  opts: { type?: OscillatorType; startGain?: number; delayMs?: number } = {},
): void {
  const { type = 'sine', startGain = 0.15, delayMs = 0 } = opts;
  const startAt = ctx.currentTime + delayMs / 1000;
  const endAt = startAt + durationMs / 1000;

  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(freq, startAt);
  // Exponential decay — feels natural; setTargetAtTime avoids click at end.
  gain.gain.setValueAtTime(startGain, startAt);
  gain.gain.exponentialRampToValueAtTime(0.0001, endAt);
  osc.connect(gain).connect(ctx.destination);
  osc.start(startAt);
  osc.stop(endAt + 0.02);
}

export function useSounds() {
  const ctorAvailable = getCtxCtor() !== null;
  const [enabled, setEnabled] = useState<boolean>(ctorAvailable && initialEnabled());
  const ctxRef = useRef<AudioContext | null>(null);

  const ensureCtx = useCallback((): AudioContext | null => {
    if (!ctorAvailable) return null;
    if (!ctxRef.current) {
      const Ctor = getCtxCtor();
      if (!Ctor) return null;
      try { ctxRef.current = new Ctor(); } catch { return null; }
    }
    // Some browsers suspend until a user gesture; resume lazily.
    if (ctxRef.current.state === 'suspended') {
      ctxRef.current.resume().catch(() => { /* ignore */ });
    }
    return ctxRef.current;
  }, [ctorAvailable]);

  const playClick = useCallback(() => {
    if (!enabled) return;
    const ctx = ensureCtx();
    if (!ctx) return;
    playTone(ctx, 820, 40, { type: 'sine', startGain: 0.08 });
  }, [enabled, ensureCtx]);

  const playCorrect = useCallback(() => {
    if (!enabled) return;
    const ctx = ensureCtx();
    if (!ctx) return;
    // C5 → E5 → G5, stacked with 60 ms stagger for a soft bell arpeggio.
    playTone(ctx, 523.25, 260, { type: 'sine', startGain: 0.12 });
    playTone(ctx, 659.25, 260, { type: 'sine', startGain: 0.10, delayMs: 60 });
    playTone(ctx, 783.99, 320, { type: 'sine', startGain: 0.09, delayMs: 120 });
  }, [enabled, ensureCtx]);

  const playIncorrect = useCallback(() => {
    if (!enabled) return;
    const ctx = ensureCtx();
    if (!ctx) return;
    // Low warm triangle — 'hmm', not 'bzzt'.
    playTone(ctx, 196, 220, { type: 'triangle', startGain: 0.13 });
    playTone(ctx, 164.81, 260, { type: 'triangle', startGain: 0.10, delayMs: 70 });
  }, [enabled, ensureCtx]);

  const playChapterComplete = useCallback(() => {
    if (!enabled) return;
    const ctx = ensureCtx();
    if (!ctx) return;
    // C5 E5 G5 C6 quick arpeggio, triumphant.
    const notes: [number, number][] = [
      [523.25, 0],
      [659.25, 120],
      [783.99, 240],
      [1046.50, 380],
    ];
    for (const [freq, delay] of notes) {
      playTone(ctx, freq, 280, { type: 'sine', startGain: 0.14, delayMs: delay });
    }
  }, [enabled, ensureCtx]);

  const toggle = useCallback(() => {
    setEnabled(prev => {
      const next = !prev;
      try { localStorage.setItem(LS_KEY, next ? '1' : '0'); } catch { /* ignore */ }
      return next;
    });
  }, []);

  return {
    enabled,
    available: ctorAvailable,
    toggle,
    playClick,
    playCorrect,
    playIncorrect,
    playChapterComplete,
  };
}
